
/// <amd-module name="Pipelines/Jira/Component/JiraConfigurationComponent" />
import * as React from "react";

import { IJiraConfigurationData, IJiraQueryParameters } from "Pipelines/Jira/Common/Types";
import * as Resources from "Pipelines/Jira/Resources";
import { IJiraConfigurationService } from "Pipelines/Jira/Service/JiraConfigurationService";
import { Utility } from "Pipelines/Jira/Utility/Utility";

import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Dialog } from "VSSUI/Dialog";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Image } from "VSSUI/Image";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { renderSimpleCell, ColumnFill, Table, ITableColumn, TableCell } from "VSSUI/Table";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";

import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { TeamFoundationHostType } from "VSS/Platform/Context";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssLocationService, getAssetLocation } from "VSS/Platform/Location";
import { combineUrlPaths } from "VSS/Platform/Util/Url";

export class JiraConfigurationComponent extends VssComponent {

    private _configurations: ObservableArray<IJiraConfigurationData>;
    private _deleteErrorMessage: ObservableValue<string>;
    private _initializeDataErrorMessage: string;

    public constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        // Store the query parameters like jwt and xdm_e sent by jira to our Jira UX api.
        // For further api calls, use this params for authentication.
        this._jiraQueryParams = Utility.getJiraSearchQueryParams();

        // Initialize data
        const jiraService = this.context.pageContext.getService<IJiraConfigurationService>("IJiraConfigurationService");
        const { configurations, deleteErrorMessage, initializeDataErrorMessage } = jiraService.initializeConfigurations(this.context.pageContext);
        this._configurations = configurations;
        this._deleteErrorMessage = deleteErrorMessage;
        this._initializeDataErrorMessage = initializeDataErrorMessage;

        // Set deployment urls
        this._aexDeploymentUrl = this._getServiceDeploymentUrl(this._aexServiceInstanceId);
        this._tfsDeploymentUrl = this._getServiceDeploymentUrl(this._tfsServiceInstanceId);

        this._showDeleteConfigurationDialog = new ObservableValue(false);
    }

    public render(): JSX.Element {
        return (
            <Observer configurations={this._configurations}>
                {(props: { configurations: IJiraConfigurationData[] }) => {
                    return (
                        <Page className={"flex-grow jira-configuration-page-content"}>
                            {this._getHeader(props.configurations)}
                            <div className={"page-content"}>
                                {this._getContent(props.configurations)}
                            </div>
                            {this._getDeleteConfigurationConfirmationDialog()}
                        </Page>
                    );
                }}
            </Observer>

        );
    }

    private _getContent(configurations: IJiraConfigurationData[]): JSX.Element {
        if (this._initializeDataErrorMessage) {
            return this._getDayZeroErrorContent();
        }
        else if (configurations.length === 0) {
            return this._getDayZeroContent();
        }
        else {
            return <div>
                        {this._getDeleteErrorMessageCard()}
                        {this._getConfigurationsTable(configurations)}
                        {this._getBuildReleaseInfoMessageCard()}
                    </div>;
        }
    }

    private _getHeader(configurations: IJiraConfigurationData[]): JSX.Element {
        let commandBarItems: IHeaderCommandBarItem[] = [];

        if (!this._initializeDataErrorMessage && configurations.length > 0) {
            commandBarItems.push({
                id: "add",
                isPrimary: true,
                text: Resources.AddOrganization,
                onActivate: (menuItem: any) => { this._onAddProjectClick() }
            });
        }

        return <Header
            className={"no-v-padding configurations-header"}
            titleClassName={"configuration-title"}
            description={this._getLink(Resources.AzurePipelineDescription)}
            descriptionClassName={"azure-devops-description"}
            titleIconProps={{ render: this._renderHeaderIcon }}
            title={Resources.AzurePipelinesIntegration}
            titleSize={TitleSize.Large}
            commandBarItems={commandBarItems}
            commandBarClassName={"configuraions-commandBar"}
            separator={true}
        />;
    }

    private _getDayZeroContent(): JSX.Element {
        return <ZeroData
                    className={"jira-configuration-zero-data-card"}
                    imagePath={getAssetLocation(this.context.pageContext, "ms.vss-pipelines-jira-web/web/zerodata-jira-configuration.png")}
                    imageAltText={Resources.AddFirstOrganizationTitle}
                    primaryText={Resources.AddFirstOrganizationTitle}
                    secondaryText={this._getLink(Resources.AddFirstOrganizationDescription)}
                    actionType={ZeroDataActionType.ctaButton}
                    actionText={Resources.AddOrganization} 
                    onActionClick={this._onAddProjectClick} />;
    }

    private _getDayZeroErrorContent(): JSX.Element {
        return <ZeroData
                    imagePath={getAssetLocation(this.context.pageContext, "ms.vss-pipelines-jira-web/web/jira-configuration-error.png")}
                    imageAltText={Resources.AddFirstOrganizationTitle}
                    primaryText={Resources.UnableToConnect}
                    secondaryText={Resources.UnableToConnectRetry}
                    actionType={ZeroDataActionType.ctaButton}
                    actionText={Resources.Retry} 
                    onActionClick={this._retry} />;
    }

    private _getLink(htmlContent: string): JSX.Element {
        return <span dangerouslySetInnerHTML={{ __html: htmlContent }} />;
    }

    private _getDeleteErrorMessageCard(): JSX.Element {
        return <Observer errorMessage={this._deleteErrorMessage}>
                    {(props: { errorMessage: string}) => {
                        return !!props.errorMessage && <MessageCard className={"configurations-message-card"} severity={MessageCardSeverity.Error}>
                                    {props.errorMessage}
                                </MessageCard>;
                    }}
                </Observer>;
    }

    private _getConfigurationsTable(configurations: IJiraConfigurationData[]): JSX.Element {
        return <Card className="bolt-table-card configurations-card" contentProps={{ contentPadding: false }}>
                    <Table
                        columns={this._getTableColumns()}
                        itemProvider={new ArrayItemProvider(configurations)}
                        showLines={true}
                        pageSize={30}
                    />
                </Card>;
    }

    private _getBuildReleaseInfoMessageCard(): JSX.Element {
        return <MessageCard className={"configurations-message-card"} severity={MessageCardSeverity.Info}>
                {this._getLink(Resources.BuildAndReleaseInfo)}
            </MessageCard>;
    }

    private _renderHeaderIcon = (): JSX.Element => {
        return <Image
            alt={"Azure DevOps"}
            className={"header-image"}
            src={getAssetLocation(this.context.pageContext, "ms.vss-pipelines-jira-web/web/azure-pipelines.png")} />;
    }

    private _retry = (): void => {
        location.reload();
    }

    private _onAddProjectClick = () => {
        if (this._aexDeploymentUrl) {
            let jiraAcquisitionUrl: string = combineUrlPaths(this._aexDeploymentUrl, `/go/signup?product=jira&jiraAccountUrl=${this._jiraQueryParams.xdm_e}&jwt=${this._jiraQueryParams.jwt}`);
            
            const child: Window | null = window.open(jiraAcquisitionUrl);
            if (child) {
                const interval = setInterval(function () {
                    if (child.closed) {
                        clearInterval(interval);
                        location.reload();
                    }
                }, 500);
            }
            else{
                $DEBUG && console.error("Failure in opening a new window");
            }
        }
    }

    private _getServiceDeploymentUrl(serviceInstanceId: string): string {
        const vssLocationService = this.context.pageContext.getService<IVssLocationService>("IVssLocationService");

        return vssLocationService.getServiceLocation(serviceInstanceId, TeamFoundationHostType.Deployment);
    }

    private _getTableColumns(): ITableColumn<IJiraConfigurationData>[] {
        const columns: ITableColumn<IJiraConfigurationData>[] = [
            {
                id: "hostName",
                name: Resources.OrganizationName,
                width: 200,
                renderCell: renderSimpleCell,
                className: "fontWeightSemiBold"
            },
            ColumnFill,
            {
                id: "delete",
                width: 40,
                renderCell: this._renderDeleteCell
            }
        ];

        return columns;
    }

    private _renderDeleteCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IJiraConfigurationData>, tableItem: IJiraConfigurationData): JSX.Element => {
        return (
            <TableCell className="bolt-table-cell-side-action" columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div>
                    <Button
                        ariaLabel={Resources.Delete}
                        className="bolt-table-cell-content-reveal"
                        subtle={true}
                        excludeTabStop={true}
                        iconProps={{
                            iconName: "Delete",
                            className: "fontSizeML"
                        }}
                        onClick={event => {
                            this._onDeleteConfiguration(tableItem, event);
                        }}
                    />
                </div>
            </TableCell>
        );
    }

    private _getDeleteConfigurationConfirmationDialog(): JSX.Element {
        return (
            <Observer isDialogOpen={this._showDeleteConfigurationDialog}>
                {
                    (props: { isDialogOpen: boolean }) => {
                        return props.isDialogOpen ? (
                            <Dialog
                                titleProps={{ text: Resources.DeleteConfigurationDialogTitle, size: TitleSize.Large }}
                                onDismiss={() => { this._showDeleteConfigurationDialog.value = false; }}
                                footerButtonProps={[
                                    {
                                        text: Resources.Cancel,
                                        onClick: () => {
                                            this._showDeleteConfigurationDialog.value = false;
                                        }
                                    },
                                    { 
                                        text: Resources.Delete,
                                        primary: true,
                                        onClick: () => {
                                            this._deleteAccountConnection(this._selectedConfiguration)
                                        } 
                                    }
                                ]}
                            >
                                <div>
                                    { format(Resources.DeleteConfigurationDialogMessage, this._selectedConfiguration.hostName) }
                                </div>
                            </Dialog>
                        ) : null;
                    }
                }
            </Observer>
        );
    }

    private _onDeleteConfiguration(configuration: IJiraConfigurationData, event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>): void {
        event.preventDefault();

        this._selectedConfiguration = configuration;
        this._showDeleteConfigurationDialog.value = true;
    }

    private _deleteAccountConnection(configuration: IJiraConfigurationData): void {
        if (this._tfsDeploymentUrl) {
            const jiraService = this.context.pageContext.getService<IJiraConfigurationService>("IJiraConfigurationService");
            jiraService.deleteAccountConfiguration(configuration, this._jiraQueryParams, this._tfsDeploymentUrl);
        }
        this._showDeleteConfigurationDialog.value = false;
    }
    
    private _selectedConfiguration: IJiraConfigurationData;
    private _aexDeploymentUrl: string;
    private _tfsDeploymentUrl: string;
    private _jiraQueryParams: IJiraQueryParameters;
    private _showDeleteConfigurationDialog: ObservableValue<boolean>;
    private _aexServiceInstanceId = "00000041-0000-8888-8000-000000000000";
    private _tfsServiceInstanceId = "00025394-6065-48ca-87d9-7f5672854ef7";
}

VssComponent.register("ms.vss-pipelines-jira-web.jiraConfigurationComponent", JiraConfigurationComponent);
